import React from "react";

import { useTranslation } from "react-i18next";

import RectangleLoader from "@/shared/components/loaders/RectangleLoader";

import {
  CreditCardIcon,
  DevicesFotaIcon,
  EventIcon,
  PowerSettingsNewIcon,
  TollIcon,
  TruckOutlineIcon
} from "@dotnet/telematics-design-system";

import {
  CONDITION_FAILURE_REASONS,
  ELIGIBILITY_CONDITIONS,
  REQUIREMENT_STATUS
} from "../../types/enums";
import { EligibilityCondition } from "../../types/interfaces";
import RequirementsCard from "./RequirementsCard/RequirementsCard";

interface ConditionConfig {
  icon: React.JSX.Element;
  titleKey: string;
}

interface RequirementsSectionProps {
  conditions: EligibilityCondition[];
  showLoader: boolean;
  canSeeCreditsRequirement: boolean;
}

const RequirementsSection = ({
  showLoader,
  conditions,
  canSeeCreditsRequirement
}: RequirementsSectionProps) => {
  const { t } = useTranslation();

  const CONDITION_CONFIG: Record<string, ConditionConfig> = {
    [ELIGIBILITY_CONDITIONS.Ignition]: {
      icon: <PowerSettingsNewIcon />,
      titleKey: "Download monitoring##requirements##ignition"
    },
    [ELIGIBILITY_CONDITIONS.VehicleActive]: {
      icon: <TruckOutlineIcon />,
      titleKey: "Download monitoring##requirements##vehicle activated"
    },
    [ELIGIBILITY_CONDITIONS.ImeiAssigned]: {
      icon: <DevicesFotaIcon />,
      titleKey: "Download monitoring##requirements##imei"
    },
    [ELIGIBILITY_CONDITIONS.CompanyCardActive]: {
      icon: <CreditCardIcon />,
      titleKey: "Download monitoring##requirements##company card"
    },
    [ELIGIBILITY_CONDITIONS.ScheduleAssigned]: {
      icon: <EventIcon />,
      titleKey: "Download monitoring##requirements##schedule assigned"
    },
    ...(canSeeCreditsRequirement
      ? {
          [ELIGIBILITY_CONDITIONS.CreditsAvailable]: {
            icon: <TollIcon />,
            titleKey: "Download monitoring##requirements##credits available"
          }
        }
      : {})
  };
  const getConditionStatus = (
    condition: EligibilityCondition
  ): REQUIREMENT_STATUS => {
    if (condition.passed) {
      return REQUIREMENT_STATUS.Success;
    }

    if (
      condition.reason === CONDITION_FAILURE_REASONS.IgnitionNotReportedRecently
    ) {
      return REQUIREMENT_STATUS.Unknown;
    }

    return REQUIREMENT_STATUS.Error;
  };

  return showLoader ? (
    <RectangleLoader height="204px" width="100%" />
  ) : (
    <>
      {conditions.map(condition => {
        const config = CONDITION_CONFIG[condition.condition];

        if (!config) {
          return null;
        }

        return (
          <RequirementsCard
            key={condition.condition}
            icon={config.icon}
            title={t(config.titleKey)}
            status={getConditionStatus(condition)}
            statusDescription={
              condition.reason
                ? t(
                    `Download monitoring##requirements##reason##${condition.reason}`
                  )
                : undefined
            }
          />
        );
      })}
    </>
  );
};

export default RequirementsSection;
